/**
 * Package upsell
 *
 * Kdyz zakaznik pridava do kosiku jediny kus produktu (na detailu i ve vypisu),
 * zachytime odeslani formulare a nejdriv otevreme modal s nabidkou 2 a 3 baleni.
 * Potvrzeni v modalu prida zvolene mnozstvi, zavreni modalu prida puvodni pocet
 * kusu - pridani do kosiku se tedy nikdy neztrati, modal ho jen doplni o nabidku.
 *
 * Obsah modalu (ceny, slevy, cena za kapsli) pochazi ze sekce 'package-upsell'
 * dotazene pres Section Rendering API, takze vsechny vypocty zustavaji v Liquidu
 * a odpovidaji package selectoru na detailu produktu.
 */
(function () {
  const MODAL_ID = 'PackageUpsellModal';
  const SECTION_CLASS = 'section-group-overlay-package-upsell';

  // Obsah sekce podle handle produktu - jeden produkt nacitame jen jednou.
  const contentCache = new Map();

  let sectionId = null;

  // Stav rozpracovaneho pridani do kosiku, ktere modal drzi.
  let pending = null;

  function getModal() {
    return document.getElementById(MODAL_ID);
  }

  function getSectionId() {
    if (sectionId !== null) return sectionId;

    const sectionElement = document.querySelector(`.${SECTION_CLASS}`);
    sectionId = sectionElement ? FoxTheme.utils.getSectionId(sectionElement) : false;

    return sectionId;
  }

  function isCartPageFlow() {
    return document.body.classList.contains('cart-template') || FoxTheme.settings.cartType === 'page';
  }

  function getQuantityInput(form) {
    const field = form.elements.namedItem('quantity');
    if (!field) return null;

    // RadioNodeList kdyz je ve formulari vic poli se stejnym jmenem (napr. zbyly
    // hidden input od package selectoru) - zajima nas to viditelne.
    if (typeof RadioNodeList !== 'undefined' && field instanceof RadioNodeList) {
      return Array.from(field).find((input) => input.type !== 'hidden') || field[0];
    }

    return field;
  }

  /**
   * Kolik kusu by se do kosiku pridalo bez modalu. Package selector na detailu
   * mnozstvi nasobi podle zvoleneho baleni, takze jeho multiplier zapocitame.
   */
  function getEffectiveQuantity(form) {
    const input = getQuantityInput(form);
    const quantity = input ? parseInt(input.value, 10) || 1 : 1;

    const section = form.closest('[data-section]') || document;
    const selector = section.querySelector('.package-selector');
    const multiplier = selector ? parseInt(selector.dataset.multiplier, 10) || 1 : 1;

    return quantity * multiplier;
  }

  function productSectionUrl(form) {
    const handle = form.dataset.packageUpsell;
    const root = (FoxTheme.routes.root_url || '/').replace(/\/$/, '');
    const variantId = form.elements.namedItem('id')?.value;

    let url = `${root}/products/${handle}?section_id=${getSectionId()}`;
    if (variantId) url += `&variant=${variantId}`;

    return url;
  }

  async function loadContent(form) {
    const handle = form.dataset.packageUpsell;
    if (contentCache.has(handle)) return contentCache.get(handle);

    // Na detailu produktu je template vyrenderovany rovnou v overlay skupine.
    const inlineTemplate = document.querySelector(
      `#PackageUpsell-content[data-product-handle="${CSS.escape(handle)}"]`
    );
    if (inlineTemplate) {
      contentCache.set(handle, inlineTemplate);
      return inlineTemplate;
    }

    const response = await fetch(productSectionUrl(form));
    if (!response.ok) throw new Error(`Package upsell: sekci se nepodarilo nacist (${response.status})`);

    const template = new DOMParser()
      .parseFromString(await response.text(), 'text/html')
      .querySelector('#PackageUpsell-content');

    contentCache.set(handle, template);

    return template;
  }

  function selectOption(body, option) {
    body.querySelectorAll('.package-upsell__option').forEach((item) => {
      const isActive = item === option;
      item.classList.toggle('package-upsell__option--active', isActive);
      item.setAttribute('aria-checked', isActive ? 'true' : 'false');
    });

    const priceElement = body.querySelector('[data-package-upsell-price]');
    const compareElement = body.querySelector('[data-package-upsell-compare]');

    if (priceElement) priceElement.innerHTML = option.dataset.price || '';
    if (compareElement) {
      const compare = option.dataset.comparePrice;
      compareElement.innerHTML = compare || '';
      compareElement.hidden = !compare;
    }
  }

  function showError(body, message) {
    const errorElement = body?.querySelector('.package-upsell__error');
    if (!errorElement) return;

    errorElement.textContent = message || '';
    errorElement.hidden = !message;
  }

  function genericErrorMessage(body) {
    const errorElement = body?.querySelector('.package-upsell__error');
    return errorElement?.dataset.genericError || FoxTheme.variantStrings.unavailable;
  }

  function setLoading(body, isLoading) {
    const submitButton = body?.querySelector('[data-package-upsell-submit]');
    if (!submitButton) return;

    submitButton.classList.toggle('btn--loading', isLoading);
    submitButton.toggleAttribute('aria-disabled', isLoading);
  }

  /**
   * Prida zvolene mnozstvi do kosiku. Kopiruje chovani ProductForm z theme.js -
   * stejny endpoint, stejne udalosti, stejne otevreni cart draweru - jen s
   * mnozstvim prepsanym podle vyberu v modalu.
   *
   * Vraci chybovou hlasku, nebo null pri uspechu.
   */
  async function addToCart(form, quantity) {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );

    const formData = new FormData(form);
    formData.set('quantity', String(quantity));
    formData.append('sections', sections);
    formData.append('sections_url', window.location.pathname);

    const config = FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    const parsedState = await (await fetch(FoxTheme.routes.cart_add_url, config)).json();

    if (parsedState.status) {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, {
        source: 'package-upsell',
        productVariantId: formData.get('id'),
        errors: parsedState.errors || parsedState.description,
        message: parsedState.message,
      });

      return parsedState.description || parsedState.message || '';
    }

    const cartJson = await (await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig() })).json();
    cartJson.sections = parsedState.sections;

    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
    document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));

    return null;
  }

  function openCartDrawer(form, submitter) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    // Pri pridani z quick view nejdriv zavreme quick view, at se drawery
    // neprekryvaji - stejne jako to dela ProductForm.showCartDrawer.
    const quickViewModal = form ? form.closest('quick-view-modal') : null;
    if (quickViewModal) {
      Promise.resolve(quickViewModal.hide()).then(() => cartDrawer.show(submitter));
      return;
    }

    cartDrawer.show(submitter);
  }

  /** Potvrzeni v modalu - prida vybrany pocet baleni. */
  async function confirmSelection(body) {
    if (!pending) return;

    const selected = body.querySelector('.package-upsell__option--active');
    const quantity = selected ? parseInt(selected.dataset.quantity, 10) || 1 : pending.quantity;
    const { form, submitter } = pending;

    // Zavreni modalu uz nesmi pridat puvodni mnozstvi navic.
    pending.resolved = true;
    setLoading(body, true);

    let errorMessage = null;
    try {
      errorMessage = await addToCart(form, quantity);
    } catch (error) {
      console.error(error);
      errorMessage = genericErrorMessage(body);
    }

    if (errorMessage !== null) {
      // Pridani neproslo - modal zustava otevreny, at to muze zakaznik zopakovat.
      pending.resolved = false;
      setLoading(body, false);
      showError(body, errorMessage);
      return;
    }

    const modal = getModal();
    if (modal) await modal.hide();

    openCartDrawer(form, submitter);
  }

  /** Zavreni modalu bez vyberu - prida puvodni pocet kusu. */
  async function completePendingAdd() {
    if (!pending || pending.resolved) {
      pending = null;
      return;
    }

    const { form, quantity, submitter } = pending;
    pending = null;

    try {
      const errorMessage = await addToCart(form, quantity);
      if (errorMessage !== null) return;
    } catch (error) {
      console.error(error);
      return;
    }

    openCartDrawer(form, submitter);
  }

  function bindBody(body) {
    body.addEventListener('click', (event) => {
      const option = event.target.closest('.package-upsell__option');
      if (option) {
        selectOption(body, option);
        showError(body, '');
        return;
      }

      const submitButton = event.target.closest('[data-package-upsell-submit]');
      if (!submitButton || submitButton.hasAttribute('aria-disabled')) return;

      confirmSelection(body);
    });
  }

  async function openModal(form, submitter, quantity) {
    const modal = getModal();
    if (!modal) return false;

    const template = await loadContent(form);
    if (!template) return false;

    const body = modal.querySelector('[data-package-upsell-body]');
    body.innerHTML = '';
    body.appendChild(template.content.cloneNode(true));

    const firstOption = body.querySelector('.package-upsell__option');
    if (!firstOption) {
      body.innerHTML = '';
      return false;
    }

    pending = { form, submitter, quantity, resolved: false };

    selectOption(body, firstOption);
    modal.show(submitter);

    return true;
  }

  function handleSubmit(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.type !== 'add-to-cart-form') return;
    if (!form.dataset.packageUpsell) return;
    // Pridani spustila pilulka "Koupit s kodem" - zakaznik uz slevu zvolil.
    // Priznak rusime tady, aby platil presne na jedno pridani do kosiku.
    if (form.dataset.skipPackageUpsell) {
      delete form.dataset.skipPackageUpsell;
      return;
    }
    if (isCartPageFlow()) return;
    if (getSectionId() === false) return;
    if (pending) return;

    const quantity = getEffectiveQuantity(form);
    if (quantity !== 1) return;

    // Pridani do kosiku prebira modal - zabranime jak nativnimu odeslani, tak
    // ProductForm handleru na samotnem formulari.
    event.preventDefault();
    event.stopPropagation();

    const submitter = event.submitter;

    openModal(form, submitter, quantity)
      .then((opened) => {
        if (opened) return;

        // Modal se nepodarilo pripravit - produkt pridame standardni cestou.
        return addToCart(form, quantity).then((errorMessage) => {
          if (errorMessage === null) openCartDrawer(form, submitter);
        });
      })
      .catch((error) => console.error(error));
  }

  function init() {
    const modal = getModal();
    if (!modal) return;

    const body = modal.querySelector('[data-package-upsell-body]');
    if (!body) return;

    bindBody(body);

    modal.addEventListener('modal:handleAfterHide', () => {
      completePendingAdd();
      body.innerHTML = '';
    });

    document.addEventListener('submit', handleSubmit, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
